import { computed, type Ref } from 'vue'
import { useAsyncData } from '#app'

import builderIoService from '~/composables/builderIoService'

interface SuggestionItem {
  pil?: string
}

interface ProductSuggestionsData {
  productSku?: string
  productName?: string
  suggestions?: SuggestionItem[]
}

export const PDP_PILLS = [
  'Is this product right for me?',
  'What does this product do?',
  'How do I use this product?',
]

const sanitizeSku = (rawSku?: string) => {
  if (!rawSku || typeof rawSku !== 'string') return undefined
  return rawSku.replace(/(^["']+)|(["']+$)/g, '').trim()
}

const resolvePills = (builderPills: string[]) => {
  const normalizedBuilderPills = builderPills
    .map((pill) => pill.trim())
    .filter(Boolean)

  if (normalizedBuilderPills.length > 0) {
    return normalizedBuilderPills
  }

  return PDP_PILLS
}

export const usePdpSuggestionPills = (sku: Ref<string | undefined>) => {
  const sanitizedSku = computed(() => sanitizeSku(sku.value))
  const service = builderIoService()

  const { data, error, pending, refresh } = useAsyncData<string[]>(
    () => `pdp-product-suggestions-${sanitizedSku.value ?? 'empty'}`,
    async () => {
      if (!sanitizedSku.value) return PDP_PILLS

      try {
        const result = await service.fetchManyModels<ProductSuggestionsData>(
          'product-suggestions',
          '/',
          false,
          {
            query: {
              'data.productSku': sanitizedSku.value,
            },
            fields: [
              'data.productSku',
              'data.productName',
              'data.suggestions',
            ],
            limit: 1,
          }
        )

        const suggestions = result?.[0]?.data?.suggestions
        const builderPills = Array.isArray(suggestions)
          ? suggestions
              .map((item) => item?.pil?.trim())
              .filter((pill): pill is string => Boolean(pill))
          : []

        return resolvePills(builderPills)
      } catch {
        return PDP_PILLS
      }
    },
    {
      server: false,
      lazy: false,
      immediate: true,
      default: () => PDP_PILLS,
      watch: [sanitizedSku],
    }
  )

  return {
    pills: data,
    error,
    pending,
    refresh,
  }
}